import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button } from '@framework-controls/core';
import css from './MenuCalendar.module.scss';
import classNames from 'classnames';
import { THEME } from 'src/constants/ThemeNames';
import { strings } from 'src/localization';
import { Appointment, Advisor, Transportation } from 'src/models';
import { DateTime } from '@reynolds/datetime';
import { Calendar } from '@framework-controls/calendar';
import AppointmentCard from 'src/components/AppointmentCard/AppointmentCard';
import SVPButton from 'src/components/SVPButtons/SVPButton';
import { ViewMode } from 'src/enums';
// import { convertTime } from 'src/helpers/TimeDisplayHelper/TimeDisplayHelper';

interface IProps {
    appointment: Appointment;
    advisors: Advisor[];
    openFlag: boolean;
    // bordered wrapper for the button's selected border - used to align the topline
    calendarButtonRef?: React.RefObject<HTMLDivElement>;
    close: (e: any) => void;
    theme: string;
    showCalRef: React.RefObject<HTMLDivElement>
    locale: string;
    transIndex?: Transportation;
    colorSensitiveEnabled: boolean,
}

const MenuCalendar = ((props: IProps) => {
    // Refs are always null in react test renderer, so in order to properly test we need to set a default ref value
    const initialValue: HTMLDivElement = document.createElement('div');
    const menuCalendarRef = useRef<HTMLDivElement>(initialValue);
    const toplineAreaRef = useRef<HTMLDivElement>(null);
    const [toplineWidth, setToplineWidth] = useState<number>();
    const [showToplineRight, setShowToplineRight] = useState(false);

    /** Measures the gap between the topline and the button's visible border so the join stays flush. */
    useLayoutEffect(function measureToplineOnOpenAndResize() {
        if (!props.openFlag) {
            return;
        }
        // the click target is narrower than the bordered wrapper, so measure against the wrapper instead
        const measureTopline = () => {
            const buttonEl = props.calendarButtonRef?.current;
            const buttonRect = buttonEl?.getBoundingClientRect();
            if (!toplineAreaRef.current || !menuCalendarRef.current || !buttonEl || !buttonRect?.width) {
                return;
            }
            // Under 600px the flyout has no border and the top line is hidden.
            if (!parseFloat(getComputedStyle(menuCalendarRef.current).borderLeftWidth)) {
                setToplineWidth(undefined);

                return;
            }
            const toplineAreaLeft = toplineAreaRef.current.getBoundingClientRect().left;
            const buttonStyle = getComputedStyle(buttonEl);
            // target is always the button's left border edge (2px normally, 1px Buick, 0px mobile);
            // toplineRight is just rotated 180deg, so it still reaches the same edge, growing left instead
            const targetX = buttonRect.left + (parseFloat(buttonStyle.borderLeftWidth) || 0);
            const gap = targetX - toplineAreaLeft;
            setShowToplineRight(gap < 0);
            setToplineWidth(Math.round(Math.abs(gap) * 100) / 100);
        };
        // the flyout may still be laying out on the very next frame, so wait a second one:
        // frame 1 lets layout settle, frame 2 reads the settled DOM
        const scheduleMeasurement = () => {
            let secondFrame: number | undefined = undefined;
            const firstFrame = requestAnimationFrame(() => {
                secondFrame = requestAnimationFrame(measureTopline);
            });

            return () => {
                cancelAnimationFrame(firstFrame);
                if (secondFrame !== undefined) {
                    cancelAnimationFrame(secondFrame);
                }
            };
        };

        let cancelPendingMeasurement = scheduleMeasurement();
        const handleResize = () => {
            // cancel any pending two-frame chain so rapid resizes don't stack measurements
            cancelPendingMeasurement();
            cancelPendingMeasurement = scheduleMeasurement();
        };
        window.addEventListener('resize', handleResize);

        return () => {
            cancelPendingMeasurement();
            window.removeEventListener('resize', handleResize);
        };
    }, [props.openFlag, props.calendarButtonRef]);

    const { advisor, date, time } = props.appointment;
    const adv = props.advisors.find((x) => x.advisorNum === advisor);
    const appointment = date + ' ' + time.slice(0, 5);
    const apptDate = new DateTime(new Date(appointment));
    // Get time elapsed
    const today = new DateTime(Date.now(), 'EST');
    let minUntil = apptDate.unitsOfTimeBetween('minutes', today);
    if (time.slice(5, 7) === 'pm'){
        minUntil += 720;
    }
    minUntil = Math.abs(minUntil);
    const dayHours = 24;
    const hourMin = 60;
    const dayMinutes = 1440;
    const daysUntil = Math.floor(minUntil / dayMinutes);
    const hoursUntil = Math.floor(minUntil / hourMin) - (dayHours * daysUntil);
    const minutes = minUntil - ((daysUntil * dayMinutes) + (hoursUntil * hourMin));
    // const dayDisplay = daysUntil + ' day(s)';
    // const hourDisplay = hoursUntil + ' hour(s)';
    // const minuteDisplay = minutes + ' minute(s)';
    const dayDisplay = daysUntil + ' ' + strings.DaysLit;
    const hourDisplay = hoursUntil + ' ' + strings.HoursLit;
    const minuteDisplay = minutes + ' ' + strings.MinutesLit;
    let apptDisplay = minuteDisplay;
    if (daysUntil > 0) {
        apptDisplay = dayDisplay + ', ' + hourDisplay + ', ' + minuteDisplay;
    }
    if (daysUntil === 0 && hoursUntil > 0) {
        apptDisplay = hourDisplay + ', ' + minuteDisplay;
    }

    const AdvImgAltTag = adv?.advisorName + ' ' + strings.Photo;

    const handleClickOutside = useCallback((e) => {
        if (!menuCalendarRef.current?.contains(e.target as Element) && !props.showCalRef.current?.contains(e.target as Element)) {
            props.close(e);
        }
    }, [menuCalendarRef, props.close, props.showCalRef]);

    useEffect(() => {
        if (props.openFlag) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [props.openFlag, handleClickOutside]);

    const CalContent = classNames(
        css.CalContent,
        {
            [css.menuMini]: props.theme === 'mini',
        },
    );
    let calLocale: 'en-US' | 'en-CA' | 'fr-CA' = 'en-US';
    if (props.locale === 'en-CA') {
        calLocale = 'en-CA';
    }
    if (props.locale === 'fr-CA') {
        calLocale = 'fr-CA';
    }

    const MenuContent = classNames(
        css.menuContent,
        {
            [css.menuMini]: props.theme === THEME.MINI,
            [css.menuAudi]: props.theme === THEME.AUDI,
            [css.menuBuick]: props.theme === THEME.BUICK,
            [css.menuChevrolet]: props.theme === THEME.CHEVROLET,
            [css.menuMB]: props.theme === THEME.MERCEDES_BENZ,
        },
    );

    const closeLitStyles = classNames(
        css.closeLit,
        {
            [css.closeLitBMW]: props.theme === 'bmw',
            [css.fontWeightBaseText]: props.theme === 'ford' || props.theme === 'honda',
            [css.closeLitIN]: props.theme === 'infiniti',
            [css.closeLitMB]: props.theme === 'mercedesBenz',
            [css.closeLitAcura]: props.theme === 'acura',
        },
    );

    const closeBtn = props.theme === THEME.AUDI ?
        <SVPButton
            id='closeBtn'
            variant={'secondaryBtnAlt'}
            message={strings.Close}
            handler={props.close}
            theme={props.theme}
            noArrow={true}
        /> :
        <Button
            variant={'text'}
            id='closeBtn'
            onClick={props.close}>
            <div className={closeLitStyles}>{strings.Close}</div>
        </Button>;

    const aptInStyles = classNames(
        css.AptIn,
        {
            [css.fontWeightBaseText]: props.theme === 'bmw' || props.theme === 'ford',
        },
    );

    const smallTextStyles = classNames(
        css.smallText,
        {
            [css.fontWeightBaseText]: props.theme === 'bmw' || props.theme === 'ford',
        },
    );


    if (!props.openFlag) {
        return null;
    }

    return (
        <div id='CalendarFlyoutContent' className={MenuContent} ref={menuCalendarRef} data-testid='refdiv'>
            <div className={CalContent}>
                {/* <div className={css.topline}></div>
                <div className={css.topline2}></div> */}
                <div className={css.toplineArea} ref={toplineAreaRef} data-testid='toplineArea-div'>
                    {showToplineRight ?
                        (<div className={css.toplineRight} data-testid='toplineRight-div'
                            style={toplineWidth === undefined ? undefined : { width: `${toplineWidth}px` }}>
                        </div>) :
                        (<div className={css.toplineLeft} data-testid='toplineLeft-div'
                            style={toplineWidth === undefined ? undefined : { width: `${toplineWidth}px` }}>
                        </div>)}
                </div>
                <div className={css.apptCard}>
                    <AppointmentCard
                        appointment={props.appointment}
                        advisorPhotoPath={props.appointment.advImage}
                        advImgAltTag={AdvImgAltTag}
                        viewMode={ViewMode.Appt}
                        theme={props.theme}
                        transIndex={props.transIndex}
                        locale={props.locale}
                        colorSensitiveEnabled={props.colorSensitiveEnabled}
                    />
                </div>
                <span id='apptIn' className={aptInStyles}>{strings.ApptIn}
                    <div className={smallTextStyles}>{apptDisplay}</div></span>
                <div className={classNames(css.calendar, { [css.calendarAudi]: props.theme === THEME.AUDI })}>
                    <Calendar
                        id='appointment'
                        date={apptDate}
                        // disablePast={true}
                        // outlinedDays={[apptDate]}
                        dayLabelType={props.theme === 'chevrolet' || props.theme === 'honda' ||
                            props.theme === 'buick' ? 'char' : 'abbr'}
                        // locale='en-US'
                        locale={calLocale}
                        selectedDayLiteral={strings.YourApptDate}
                        readOnly
                        size='large'
                    />
                </div>
                <div className={css.closeBtn}>
                    {closeBtn}
                </div>
            </div>
        </div>
    );
});

export default MenuCalendar;